import {FC} from 'react';
import {Box, Button, Dialog, Typography} from '@mui/material';
import {useIntl} from 'react-intl';
import {
    phoneBadgeSx,
    phoneSheetHandleSx,
    phoneSubtitleSx,
    phoneTitleSx,
} from 'src/components/mobile/mobileDesign.sx';
import {ActionSheetDestructiveProps, ActionSheetProps} from './ActionSheet.types';
import {
    actionsSx,
    cancelSx,
    cancelWrapSx,
    destructiveSx,
    headSx,
    iconRowSx,
    identitySx,
    sheetSx,
} from './ActionSheet.sx';

/**
 * A row's actions, as a sheet.
 *
 * Every list card used to open its menu in a popover anchored to a 20px glyph in the corner of the
 * card: a target under half a fingertip, opening a box of short lines somewhere near the edge of the
 * screen. Below a desktop the same actions rise from the bottom instead — the record named at the
 * top so there is no doubt which row is about to be changed, one 56px line each, and a way out that
 * is a line of its own rather than a tap on whatever happens to be behind.
 *
 * It takes the very children the popover took. Nothing about a handler, a permission gate or a
 * nested modal changes on the way in; only where they are drawn does.
 */
export const ActionSheet: FC<ActionSheetProps> = ({
                                                      open,
                                                      onClose,
                                                      title,
                                                      subtitle,
                                                      icon,
                                                      children,
                                                  }) => {
    const intl = useIntl();

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth={false}
            sx={sheetSx}
        >
            <Box sx={phoneSheetHandleSx}/>
            {(title || subtitle) && (
                <Box sx={headSx}>
                    {icon && <Box sx={phoneBadgeSx}>{icon}</Box>}
                    <Box sx={identitySx}>
                        {/* A div rather than a paragraph: a title may be a cell the list already
                            renders, and those carry their own blocks. */}
                        {title && (
                            <Typography
                                component="div"
                                sx={phoneTitleSx}
                            >
                                {title}
                            </Typography>
                        )}
                        {subtitle && (
                            <Typography
                                component="div"
                                sx={phoneSubtitleSx}
                            >
                                {subtitle}
                            </Typography>
                        )}
                    </Box>
                </Box>
            )}
            <Box sx={actionsSx}>{children}</Box>
            <Box sx={cancelWrapSx}>
                <Button
                    variant="outlined"
                    sx={cancelSx}
                    onClick={onClose}
                >
                    {intl.formatMessage({id: 'cancel'})}
                </Button>
            </Box>
        </Dialog>
    );
};

/**
 * The part of a menu that destroys something.
 *
 * Wrapped rather than styled at the call site, so the same action keeps the popover's own markup on
 * a desktop and only the sheet puts it below a rule and in the brand's red.
 */
export const ActionSheetDestructive: FC<ActionSheetDestructiveProps> = ({children}) => (
    <Box sx={destructiveSx}>{children}</Box>
);

/** Actions that are icons and carry no label of their own, kept on one line at a finger's size. */
export const ActionSheetIcons: FC<ActionSheetDestructiveProps> = ({children}) => (
    <Box sx={iconRowSx}>{children}</Box>
);
